<template>
    <div class="observe-v2 wrapper">
        <proTitle></proTitle>
        <div v-loading="pageLoading">
            <el-empty v-if="ready && !enabledList.length" description="暂无已启用的观思问，请先在「观思问配置」中启用"></el-empty>

            <template v-else-if="enabledList.length">
                <div class="toolbar">
                    <el-button size="small" icon="el-icon-download" @click="downloadQrcode">下载二维码</el-button>
                    <el-button size="small" icon="el-icon-s-data" :loading="exporting" @click="exportStats">导出统计</el-button>
                    <el-button size="small" icon="el-icon-document" :loading="exporting" @click="exportDetail">导出详情</el-button>
                </div>

                <div class="date-bar-wrap" ref="dateBarWrap" @wheel.prevent="onDateBarWheel" v-if="dateList.length">
                    <div class="date-bar">
                        <div class="date-bar-item" v-for="date in dateList" :key="date"
                            :class="{ 'is-active': date === selectedDate, 'is-unopened': !isOpened(date) }"
                            @click="selectDate(date)">
                            <div class="date-bar-date">{{ formatDateLabel(date) }}</div>
                            <div class="date-bar-week">
                                <span>{{ weekdayLabel(date) }}<template v-if="date === today"> · 今天</template></span>
                                <span class="date-bar-status" :class="chipStatusClass(date)">{{ chipStatusText(date) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="date-empty" v-else>尚未配置作答日期</div>

                <div class="stat-grid">
                    <div class="stat-card">
                        <div class="stat-value">{{ statValue('total') }}</div>
                        <div class="stat-label">应提交人数</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-value is-green">{{ statValue('submitted') }}</div>
                        <div class="stat-label">已提交</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-value is-orange">{{ statValue('unsubmitted') }}</div>
                        <div class="stat-label">未提交</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-value">{{ statRate }}</div>
                        <div class="stat-label">提交率</div>
                    </div>
                </div>

                <div class="filter-seg">
                    <button type="button" class="filter-seg-btn" :class="{ 'is-active': filter === 'all' }" @click="setFilter('all')">全部</button>
                    <button type="button" class="filter-seg-btn" :class="{ 'is-active': filter === 'done' }" @click="setFilter('done')">已提交</button>
                    <button type="button" class="filter-seg-btn" :class="{ 'is-active': filter === 'todo' }" @click="setFilter('todo')">未提交</button>
                </div>

                <div class="split" v-loading="dayLoading">
                    <div class="pane detail-pane">
                        <div class="pane-title">
                            <span>提交详情</span>
                            <span class="pane-count" v-if="phaseText">所属阶段：{{ phaseText }}</span>
                        </div>
                        <div class="detail-body" v-loading="detailLoading">
                            <template v-if="selectedUser">
                                <div class="detail-meta">
                                    <div class="avatar" :style="{ background: avatarColor(selectedUser) }">{{ (selectedUser.userName || '?').charAt(0) }}</div>
                                    <div>
                                        <div class="detail-title">{{ selectedUser.userName }}</div>
                                        <div class="detail-sub">{{ detailSub(selectedUser) }}</div>
                                    </div>
                                    <span class="status-capsule" :class="isSubmitted(selectedUser) ? 'is-submitted' : 'is-unsubmitted'">
                                        {{ isSubmitted(selectedUser) ? '已提交' : '未提交' }}
                                    </span>
                                </div>
                                <div class="answer-scroll" v-if="isSubmitted(selectedUser) && detail">
                                    <div class="answer-block" v-for="block in answerBlocks" :key="block.key">
                                        <div class="answer-label">{{ block.label }}</div>
                                        <div class="answer-prompt" v-if="block.prompt">{{ block.prompt }}</div>
                                        <div class="answer-text">{{ block.value || '（未填写）' }}</div>
                                    </div>
                                </div>
                                <div class="empty-block" v-else-if="!isSubmitted(selectedUser)">该用户当日尚未提交观思问</div>
                                <div class="empty-block" v-else-if="!detailLoading">暂无提交详情</div>
                            </template>
                            <div class="empty-block" v-else>{{ detailEmptyText }}</div>
                        </div>
                    </div>
                    <div class="pane user-pane">
                        <div class="pane-title">
                            <span>提交用户</span>
                            <span class="pane-count">{{ visibleUsers.length }}人</span>
                        </div>
                        <div class="user-search">
                            <el-input v-model.trim="keyword" size="small" clearable placeholder="搜索用户..."
                                prefix-icon="el-icon-search"></el-input>
                        </div>
                        <div class="user-list">
                            <div class="user-item" v-for="user in visibleUsers" :key="userKey(user)"
                                :class="{ 'is-active': selectedKey === userKey(user) }"
                                @click="selectUser(user)">
                                <div class="avatar sm" :style="{ background: avatarColor(user) }">{{ (user.userName || '?').charAt(0) }}</div>
                                <div class="user-info">
                                    <div class="user-name">{{ user.userName }}</div>
                                    <div class="user-sub">{{ userSub(user) }}</div>
                                </div>
                                <span class="status-capsule" :class="isSubmitted(user) ? 'is-submitted' : 'is-unsubmitted'">
                                    {{ isSubmitted(user) ? '已提交' : '未提交' }}
                                </span>
                            </div>
                            <div class="empty-block sm" v-if="!visibleUsers.length">{{ userEmptyText }}</div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
        <div ref="qrcode" class="qrcode-box"></div>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import { saveAs } from 'file-saver';
    import QRCode from 'qrcodejs2';
    import {
        isApiSuccess,
        apiMsg,
        apiData,
        formatDateLabel,
        weekdayLabel,
        todayKey,
        getRangeKeys,
        AVATAR_COLORS,
        scrollDateChipToCenter,
    } from './log-moment-shared.js';

    const DAILY_LIMIT = 2000;

    export default {
        data() {
            return {
                pageLoading: false,
                ready: false,
                dayLoading: false,
                detailLoading: false,
                enabledList: [],
                dateList: [],
                selectedDate: '',
                rateMap: {},
                records: [],
                dayStats: null,
                filter: 'all',
                keyword: '',
                selectedKey: '',
                detail: null,
                today: todayKey(),
                exporting: false,
                exportPollTimer: null,
                exportAborted: false,
                qrInited: false,
                daySeq: 0,
                detailSeq: 0,
                rateSeq: 0,
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            projectId() {
                return this.currentProject && this.currentProject.id;
            },
            visibleUsers() {
                let list = this.records || [];
                if (this.filter === 'done') {
                    list = list.filter((user) => this.isSubmitted(user));
                } else if (this.filter === 'todo') {
                    list = list.filter((user) => !this.isSubmitted(user));
                }
                let q = (this.keyword || '').trim().toLowerCase();
                if (!q) return list;
                return list.filter((user) => {
                    let hay = [user.userName, user.schoolName, user.stageName, user.phone].join(' ').toLowerCase();
                    return hay.indexOf(q) >= 0;
                });
            },
            selectedUser() {
                return this.visibleUsers.find((user) => this.userKey(user) === this.selectedKey) || null;
            },
            phaseText() {
                if (this.detail && this.detail.stageName) return this.detail.stageName;
                if (this.selectedUser && this.selectedUser.stageName) return this.selectedUser.stageName;
                return '';
            },
            answerBlocks() {
                let questions = (this.detail && this.detail.questions) || {};
                let answers = (this.detail && this.detail.answers) || {};
                return [
                    { key: 'observe', label: questions.observeLabel || '观', prompt: questions.observeText || '', value: answers.observe },
                    { key: 'reflect', label: questions.reflectLabel || '思', prompt: questions.reflectText || '', value: answers.think },
                    { key: 'question', label: questions.questionLabel || '问', prompt: questions.questionText || '', value: answers.ask },
                ];
            },
            statRate() {
                if (!this.selectedDate || !this.dayStats) return '-';
                let hit = this.rateMap[this.selectedDate];
                if (hit && hit.rate != null) return this.formatRate(hit.rate);
                if (!this.dayStats.total) return '0.00%';
                return (this.dayStats.submitted / this.dayStats.total * 100).toFixed(2) + '%';
            },
            detailEmptyText() {
                if (!this.selectedDate) return '暂无可查看的作答日期';
                if (this.keyword && this.records.length) return '未找到匹配用户';
                if (this.filter === 'todo') return '暂无未提交用户';
                return '请从右侧选择用户查看提交详情';
            },
            userEmptyText() {
                if ((this.keyword || '').trim() && this.records.length) return '未找到匹配用户';
                if (this.filter === 'done') return '暂无已提交用户';
                if (this.filter === 'todo') return '暂无未提交用户';
                return '暂无用户';
            },
        },
        watch: {
            keyword() {
                this.ensureSelection(false);
            },
        },
        methods: {
            formatDateLabel,
            weekdayLabel,
            isOpened(date) {
                return !!date && date <= this.today;
            },
            isSubmitted(user) {
                return !!(user && user.submitStatus === 'submitted');
            },
            userKey(user) {
                return String((user && user.userId) || '');
            },
            formatRate(rate) {
                let n = Number(rate);
                if (Number.isNaN(n)) return '';
                return n.toFixed(2) + '%';
            },
            chipStatusText(date) {
                if (!this.isOpened(date)) return '未开启';
                if (date === this.today) return '今日';
                let hit = this.rateMap[date];
                if (hit && hit.rate != null) return this.formatRate(hit.rate);
                return '已开启';
            },
            chipStatusClass(date) {
                if (!this.isOpened(date)) return 'is-unopened-tag';
                if (date === this.today) return 'is-today';
                return 'is-opened';
            },
            statValue(key) {
                if (!this.selectedDate || !this.dayStats) return '-';
                let n = this.dayStats[key];
                return n == null ? 0 : n;
            },
            avatarColor(user) {
                if (!this.isSubmitted(user)) return '#C0C4CC';
                let n = Number(user.userId) || 0;
                return AVATAR_COLORS[n % AVATAR_COLORS.length];
            },
            userSub(user) {
                let parts = [];
                if (user.schoolName) parts.push(user.schoolName);
                if (user.stageName) parts.push(user.stageName);
                return parts.join(' · ');
            },
            detailSub(user) {
                let parts = [];
                if (user.schoolName) parts.push(user.schoolName);
                if (this.isSubmitted(user) && user.submitTime) parts.push(user.submitTime + ' 提交');
                return parts.join(' · ');
            },
            onDateBarWheel(e) {
                let wrap = this.$refs.dateBarWrap;
                if (wrap) wrap.scrollLeft += e.deltaY || e.deltaX;
            },
            scrollActiveDate() {
                this.$nextTick(() => {
                    scrollDateChipToCenter(this.$refs.dateBarWrap, '.date-bar-item.is-active');
                });
            },
            // minDays 按阶段周次区间展开每一天；fixedDates 直接用作答日
            buildDateList(list, stageMap) {
                let set = {};
                (list || []).forEach((item) => {
                    if (item.answerMode === 'minDays') {
                        let stage = stageMap[item.stageId] || {};
                        (stage.periods || []).forEach((period) => {
                            getRangeKeys(period.startDate, period.endDate).forEach((date) => {
                                if (date) set[date] = true;
                            });
                        });
                        return;
                    }
                    (item.answerDates || []).forEach((date) => {
                        if (date) set[date] = true;
                    });
                });
                return Object.keys(set).sort();
            },
            pickDefaultDate(dates) {
                if (dates.indexOf(this.today) >= 0) return this.today;
                let opened = dates.filter((date) => this.isOpened(date));
                if (!opened.length) return '';
                return opened[opened.length - 1];
            },
            async initPage() {
                if (!this.projectId) {
                    this.ready = true;
                    this.$message.warning('请先选择项目');
                    return;
                }
                this.pageLoading = true;
                try {
                    let [gswRes, stageRes] = await Promise.all([
                        this.$Api.StageV2.getGswList({ projectId: this.projectId }),
                        this.$Api.StageV2.getStageList({ projectId: this.projectId, enabled: 1 }),
                    ]);
                    if (!isApiSuccess(gswRes)) {
                        this.$message.error(apiMsg(gswRes) || '获取观思问配置失败');
                        this.enabledList = [];
                        return;
                    }
                    let stageMap = {};
                    if (isApiSuccess(stageRes)) {
                        (apiData(stageRes) || []).forEach((stage) => {
                            stageMap[stage.id] = stage;
                        });
                    }
                    let list = apiData(gswRes) || [];
                    this.enabledList = list.filter((item) => Number(item.frontendEnabled) === 1);
                    this.dateList = this.buildDateList(this.enabledList, stageMap);
                    this.selectedDate = this.pickDefaultDate(this.dateList);
                    this.loadRates();
                    if (this.selectedDate) await this.loadDay();
                } catch (e) {
                    this.$message.error('获取观思问配置失败');
                    this.enabledList = [];
                } finally {
                    this.pageLoading = false;
                    this.ready = true;
                    this.$nextTick(() => {
                        this.initQrcode();
                        this.scrollActiveDate();
                    });
                }
            },
            async loadRates() {
                let dates = this.dateList.filter((date) => this.isOpened(date));
                if (!dates.length || !this.projectId) return;
                let seq = ++this.rateSeq;
                let map = {};
                try {
                    for (let i = 0; i < dates.length; i += 366) {
                        let chunk = dates.slice(i, i + 366);
                        let res = await this.$Api.StageV2.getGswDailyRates({
                            projectId: this.projectId,
                            dates: chunk.join(','),
                        });
                        if (seq !== this.rateSeq) return;
                        if (!isApiSuccess(res)) return;
                        (apiData(res) || []).forEach((item) => {
                            if (item && item.date) map[item.date] = item;
                        });
                    }
                    if (seq !== this.rateSeq) return;
                    this.rateMap = map;
                } catch (e) {
                    // 提交率失败时日期条降级为「已开启」
                }
            },
            async loadDay() {
                if (!this.selectedDate || !this.projectId) {
                    this.records = [];
                    this.dayStats = null;
                    this.selectedKey = '';
                    this.detail = null;
                    return;
                }
                let seq = ++this.daySeq;
                let showSpin = !this.pageLoading;
                if (showSpin) this.dayLoading = true;
                try {
                    let page = 1;
                    let all = [];
                    let stats = { total: 0, submitted: 0, unsubmitted: 0 };
                    while (page <= 50) {
                        let res = await this.$Api.StageV2.getGswDaily({
                            projectId: this.projectId,
                            date: this.selectedDate,
                            mode: 'detail',
                            page,
                            limit: DAILY_LIMIT,
                        });
                        if (seq !== this.daySeq) return;
                        if (!isApiSuccess(res)) {
                            this.$message.error(apiMsg(res) || '获取提交情况失败');
                            this.records = [];
                            this.dayStats = null;
                            this.selectedKey = '';
                            this.detail = null;
                            return;
                        }
                        let data = apiData(res) || {};
                        stats = {
                            total: data.total || 0,
                            submitted: data.submitted || 0,
                            unsubmitted: data.unsubmitted || 0,
                        };
                        let rows = data.records || [];
                        all = all.concat(rows);
                        if (rows.length < DAILY_LIMIT) break;
                        page += 1;
                    }
                    if (seq !== this.daySeq) return;
                    this.records = all;
                    this.dayStats = stats;
                    this.ensureSelection(true);
                } catch (e) {
                    if (seq !== this.daySeq) return;
                    this.$message.error('获取提交情况失败');
                    this.records = [];
                    this.dayStats = null;
                } finally {
                    if (seq === this.daySeq && showSpin) this.dayLoading = false;
                }
            },
            selectDate(date) {
                if (!this.isOpened(date)) {
                    this.$message.warning('该日期尚未开启，不可查看');
                    return;
                }
                if (date === this.selectedDate) return;
                this.selectedDate = date;
                this.selectedKey = '';
                this.detail = null;
                this.scrollActiveDate();
                this.loadDay();
            },
            setFilter(filter) {
                this.filter = filter;
                this.ensureSelection(false);
            },
            ensureSelection(reload) {
                let list = this.visibleUsers;
                if (!list.length) {
                    this.selectedKey = '';
                    this.detail = null;
                    return;
                }
                let current = list.find((user) => this.userKey(user) === this.selectedKey);
                if (!current) {
                    this.selectedKey = this.userKey(list[0]);
                    this.loadDetail(list[0]);
                    return;
                }
                if (reload) this.loadDetail(current);
            },
            selectUser(user) {
                if (!user) return;
                let key = this.userKey(user);
                if (key === this.selectedKey && (this.detail || !this.isSubmitted(user))) return;
                this.selectedKey = key;
                this.loadDetail(user);
            },
            async loadDetail(user) {
                if (!user || !this.isSubmitted(user)) {
                    this.detailSeq += 1;
                    this.detail = null;
                    this.detailLoading = false;
                    return;
                }
                let seq = ++this.detailSeq;
                this.detailLoading = true;
                this.detail = null;
                try {
                    let res = await this.$Api.StageV2.getGswUserDaily({
                        projectId: this.projectId,
                        userId: user.userId,
                        date: this.selectedDate,
                    });
                    if (seq !== this.detailSeq) return;
                    if (!isApiSuccess(res)) {
                        this.$message.error(apiMsg(res) || '获取提交详情失败');
                        this.detail = null;
                        return;
                    }
                    let data = apiData(res) || {};
                    this.detail = data.userId ? data : null;
                } catch (e) {
                    if (seq !== this.detailSeq) return;
                    this.$message.error('获取提交详情失败');
                    this.detail = null;
                } finally {
                    if (seq === this.detailSeq) this.detailLoading = false;
                }
            },
            initQrcode() {
                if (this.qrInited || !this.$refs.qrcode || !this.projectId) return;
                this.qrInited = true;
                new QRCode(this.$refs.qrcode, {
                    text: `https://cdnyun.cnuen.net/appv2/index.html#/user/login?type=gsw&projectId=${this.projectId}&env=Browser&t=${Date.now()}`,
                    width: 200,
                    height: 200,
                    colorDark: '#000000',
                    colorLight: '#ffffff',
                    correctLevel: QRCode.CorrectLevel.H,
                });
            },
            downloadQrcode() {
                let canvas = this.$refs.qrcode && this.$refs.qrcode.querySelector('canvas');
                if (!canvas) {
                    this.$message.error('二维码未生成');
                    return;
                }
                let link = document.createElement('a');
                link.href = canvas.toDataURL('image/png');
                link.download = `${(this.currentProject && this.currentProject.name) || '观思问'}_qrcode`;
                link.click();
            },
            waitExportPoll(ms) {
                return new Promise((resolve) => {
                    this.exportPollTimer = setTimeout(resolve, ms);
                });
            },
            async pollGswExport(taskId) {
                let start = Date.now();
                let timeout = 15 * 60 * 1000;
                while (!this.exportAborted && Date.now() - start < timeout) {
                    let resData = await this.$Api.StageV2.getGswExportStatus({ taskId });
                    if (!resData || resData.code != 200) {
                        throw new Error((resData && resData.msg) || '导出失败');
                    }
                    let data = resData.data || {};
                    if (data.status === 'SUCCESS') {
                        if (!data.fileName) throw new Error('导出失败');
                        return data.fileName;
                    }
                    if (data.status === 'FAIL') throw new Error(data.errorMsg || '导出失败');
                    await this.waitExportPoll(2000);
                }
                if (this.exportAborted) throw new Error('已取消导出');
                throw new Error('导出超时，请稍后重试');
            },
            gswDownloadStamp() {
                let d = new Date();
                let pad = (n) => String(n).padStart(2, '0');
                return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
            },
            async downloadGswExportFile(fileName, namePrefix) {
                let res = await this.$Api.StageV2.downloadGswExport({ fileName });
                let blob = res.data;
                if (blob && blob.type && blob.type.indexOf('application/json') >= 0) {
                    let text = await blob.text();
                    let json = JSON.parse(text);
                    throw new Error((json && json.msg) || '导出失败');
                }
                saveAs(blob, `${namePrefix}${this.gswDownloadStamp()}.xlsx`);
            },
            async runGswAsyncExport(createTask, namePrefix) {
                if (this.exporting) {
                    this.$message.warning('正在导出中，请稍候');
                    return;
                }
                this.exporting = true;
                this.exportAborted = false;
                let loadingInstance = this.$loading({
                    target: this.$el,
                    lock: true,
                    text: '正在导出中，请勿关闭页面...',
                    spinner: 'el-icon-loading',
                });
                try {
                    let resData = await createTask();
                    if (!resData || resData.code != 200) {
                        this.$message.error((resData && resData.msg) || '导出失败');
                        return;
                    }
                    let taskId = typeof resData.data === 'string'
                        ? resData.data
                        : (resData.data && resData.data.taskId);
                    if (!taskId) {
                        this.$message.error('导出失败');
                        return;
                    }
                    let fileName = await this.pollGswExport(taskId);
                    await this.downloadGswExportFile(fileName, namePrefix);
                } catch (e) {
                    if (!this.exportAborted) {
                        this.$message.error((e && e.message) || '导出失败');
                    }
                } finally {
                    loadingInstance.close();
                    this.exporting = false;
                    if (this.exportPollTimer) {
                        clearTimeout(this.exportPollTimer);
                        this.exportPollTimer = null;
                    }
                }
            },
            exportStats() {
                this.runGswAsyncExport(
                    () => this.$Api.StageV2.exportGswSubmissionAsync({
                        projectId: this.projectId,
                    }),
                    '观思问统计'
                );
            },
            exportDetail() {
                this.runGswAsyncExport(
                    () => this.$Api.StageV2.exportGswSubmissionDetailAsync({
                        projectId: this.projectId,
                    }),
                    '观思问详情'
                );
            },
        },
        mounted() {
            this.initPage();
        },
        beforeDestroy() {
            this.exportAborted = true;
            this.daySeq += 1;
            this.detailSeq += 1;
            this.rateSeq += 1;
            if (this.exportPollTimer) {
                clearTimeout(this.exportPollTimer);
                this.exportPollTimer = null;
            }
        },
    };
</script>

<style lang="less" scoped>
    @selected-blue: #3A7BD5;
    @selected-border: #8BB8F0;
    @border-color: #EBEEF5;
    @pane-h: 520px;
    @pane-title-h: 47px;

    .observe-v2 {
        padding: 20px 25px;
        box-sizing: border-box;
    }

    .toolbar {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-bottom: 12px;
    }

    .qrcode-box {
        display: none;
    }

    .date-empty {
        margin-bottom: 16px;
        color: #8B95A1;
        font-size: 13px;
    }

    .date-bar-wrap {
        margin-bottom: 16px;
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: thin;
        scrollbar-color: #D5D8DC transparent;

        &::-webkit-scrollbar {
            height: 4px;
        }

        &::-webkit-scrollbar-thumb {
            background: #D5D8DC;
            border-radius: 2px;
        }
    }

    .date-bar {
        display: flex;
        min-width: 100%;
    }

    .date-bar-item {
        flex: 1 0 calc(100% / 7);
        min-width: calc(100% / 7);
        box-sizing: border-box;
        text-align: center;
        padding: 10px 8px;
        cursor: pointer;
        background: #fff;
        border: 1px solid @border-color;
        border-radius: 8px;
        margin-right: 8px;

        &:last-child {
            margin-right: 0;
        }

        &:hover:not(.is-active):not(.is-unopened) {
            border-color: #C0C4CC;
        }

        &.is-active {
            border-color: @selected-border;
            border-left: 3px solid @selected-blue;
            box-shadow: 0 0 0 3px rgba(58, 123, 213, 0.1);

            .date-bar-date {
                color: @selected-blue;
            }
        }

        &.is-unopened {
            cursor: not-allowed;

            .date-bar-date,
            .date-bar-week {
                color: #C0C4CC;
            }
        }
    }

    .date-bar-date {
        font-size: 13px;
        font-weight: 600;
        color: #1F2A37;
        line-height: 20px;
    }

    .date-bar-week {
        font-size: 12px;
        color: #8B95A1;
        margin-top: 2px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        flex-wrap: wrap;
    }

    .date-bar-status {
        display: inline-block;
        padding: 0 6px;
        border-radius: 8px;
        font-size: 11px;
        line-height: 18px;

        &.is-today {
            background: #F3F8FF;
            color: @selected-blue;
        }

        &.is-opened {
            background: #EEF8F0;
            color: #3D9A5A;
        }

        &.is-unopened-tag {
            background: #F4F6F8;
            color: #8B95A1;
        }
    }

    .stat-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 12px;
        margin-bottom: 16px;
    }

    .stat-card {
        background: #fff;
        border: 1px solid @border-color;
        border-radius: 8px;
        padding: 16px 20px;
        text-align: center;
    }

    .stat-value {
        font-size: 28px;
        font-weight: 700;
        color: #1F2A37;

        &.is-green {
            color: #3D9A5A;
        }

        &.is-orange {
            color: #E6A23C;
        }
    }

    .stat-label {
        font-size: 13px;
        color: #8B95A1;
        margin-top: 4px;
    }

    .filter-seg {
        display: inline-flex;
        padding: 3px;
        background: #F0F2F5;
        border-radius: 8px;
        gap: 2px;
        margin-bottom: 16px;
    }

    .filter-seg-btn {
        padding: 6px 16px;
        border: none;
        border-radius: 6px;
        background: transparent;
        font-size: 13px;
        font-weight: 500;
        color: #606266;
        cursor: pointer;
        font-family: inherit;

        &.is-active {
            background: #fff;
            color: @selected-blue;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }
    }

    .split {
        display: grid;
        grid-template-columns: 1fr 300px;
        gap: 16px;
    }

    .pane {
        background: #fff;
        border: 1px solid @border-color;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;
    }

    .pane-title {
        height: @pane-title-h;
        padding: 0 16px;
        font-size: 14px;
        font-weight: 600;
        border-bottom: 1px solid @border-color;
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
        gap: 8px;
    }

    .pane-count {
        font-size: 12px;
        font-weight: 400;
        color: #8B95A1;
    }

    .detail-pane,
    .user-pane {
        height: @pane-h;
    }

    .detail-body {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }

    .detail-meta {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px 20px;
        flex-shrink: 0;
    }

    .detail-title {
        font-size: 16px;
        font-weight: 600;
    }

    .detail-sub {
        font-size: 12px;
        color: #8B95A1;
        margin-top: 2px;
    }

    .answer-scroll {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding: 0 20px 16px;
    }

    .answer-block {
        margin-top: 12px;
        padding: 12px 14px;
        border: 1px solid @border-color;
        border-radius: 8px;
        background: #fafbfc;
    }

    .answer-label {
        display: inline-block;
        margin-bottom: 8px;
        padding: 0 8px;
        border-radius: 4px;
        background: #F3F8FF;
        color: @selected-blue;
        font-size: 13px;
        font-weight: 600;
        line-height: 22px;
    }

    .answer-prompt {
        font-size: 12px;
        color: #8B95A1;
        line-height: 1.5;
        margin-bottom: 8px;
    }

    .answer-text {
        font-size: 14px;
        color: #606266;
        line-height: 1.8;
        white-space: pre-wrap;
    }

    .empty-block {
        min-height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #8B95A1;
        font-size: 13px;
        text-align: center;
        padding: 24px;

        &.sm {
            min-height: 80px;
        }
    }

    .user-search {
        padding: 10px 12px 0;
    }

    .user-list {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding: 8px;
    }

    .user-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        border-radius: 8px;
        cursor: pointer;
        border: 1px solid transparent;

        &:hover {
            background: #F5F7FA;
        }

        &.is-active {
            background: #fff;
            border-color: @selected-border;
            border-left: 3px solid @selected-blue;
            box-shadow: 0 0 0 3px rgba(58, 123, 213, 0.1);
        }
    }

    .user-info {
        flex: 1;
        min-width: 0;
    }

    .user-name {
        font-size: 13px;
        font-weight: 600;
    }

    .user-sub {
        font-size: 12px;
        color: #8B95A1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .status-capsule {
        display: inline-block;
        padding: 2px 10px;
        border-radius: 10px;
        font-size: 12px;
        line-height: 20px;
        margin-left: auto;
        white-space: nowrap;
        flex-shrink: 0;

        &.is-submitted {
            background: #EEF8F0;
            color: #3D9A5A;
        }

        &.is-unsubmitted {
            background: #F4F6F8;
            color: #8B95A1;
        }
    }

    .avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        font-weight: 600;
        color: #fff;
        flex-shrink: 0;

        &.sm {
            width: 32px;
            height: 32px;
            font-size: 13px;
        }
    }

    @media (max-width: 960px) {
        .split {
            grid-template-columns: 1fr;
        }

        .stat-grid {
            grid-template-columns: 1fr 1fr;
        }

        .user-pane,
        .detail-pane {
            height: auto;
            min-height: 320px;
        }
    }
</style>
